import { InjectionToken } from '@angular/core';

import type { ClarityControlCenterTabVisibility } from './clarity-control-center-tab-visibility.model';

/**
 * Every visibility decision offered to the Clarity Control Center tab strip, registered `multi: true`.
 *
 * This is the seam that keeps the shell independent of whatever decides a tab's visibility. A
 * contributor that wants a tab hidden under some condition provides exactly one
 * `ClarityControlCenterTabVisibility` with
 * `{ provide: CLARITY_CONTROL_CENTER_TAB_VISIBILITY_TOKEN, useFactory: …, multi: true }`; the shell
 * injects the whole array and matches each entry's `tabId` against the tab it is drawing. The
 * inversion is the point — the shell depends on this `type:util` library and on nothing else, so a
 * contributor can add or drop a decision without the shell changing and without the shell's library
 * ever being allowed to depend on the contributor's.
 *
 * The defaulting rule points one way and only one way: **a tab with no contribution is shown**, and a
 * tab is hidden **only while** its contributed `visible()` reads `false`. A missing contribution
 * therefore never hides anything.
 *
 * The token declares no factory, so it has no default value: an injection site where nobody has
 * contributed throws unless it asks for the array optionally. Read it as
 * `inject(CLARITY_CONTROL_CENTER_TAB_VISIBILITY_TOKEN, { optional: true }) ?? []` — with `optional`
 * the unprovided case is `null`, and the `?? []` is what turns "nobody contributed" into a strip that
 * hides nothing rather than a crash.
 */
export const CLARITY_CONTROL_CENTER_TAB_VISIBILITY_TOKEN = new InjectionToken<
	readonly ClarityControlCenterTabVisibility[]
>('CLARITY_CONTROL_CENTER_TAB_VISIBILITY');
